import { mapKeys } from 'lodash';

import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
import { FieldSchema } from '@/modules/dynamic-form/models/FieldSchema.model';
import { FieldType } from '@/modules/dynamic-form/models/FieldType.model';
import { FieldValidator } from '@/modules/dynamic-form/models/FieldValidator.model';
import { TypeDefinitionList } from '@/modules/dynamic-form/models/TypeDefinition.model';
import { UnifiedFieldRecord, UnifiedFieldRecordKey } from '@/modules/dynamic-form/models/UnifiedFieldRecord.model';
import { ValidationRules } from '@/modules/dynamic-form/models/ValidationRuleNames.model';
import { createDefaultValidators } from '@/modules/dynamic-form/utils/create-default-validators/createDefaultValidators';
import { toFieldPath } from '@/modules/dynamic-form/utils/to-field-path/toFieldPath';
import { createAddressSubFieldValidatorsMap } from '@/modules/dynamic-form/validations/address/createAddressSubFieldValidatorsMap';
import { createCollateralSubFieldValidatorsMap } from '@/modules/dynamic-form/validations/collateral/createCollateralSubFieldValidatorsMap';
import { createRequiredValidator } from '@/modules/dynamic-form/validations/commons/required.validator';
import { createAfterValidator } from '@/modules/dynamic-form/validations/date/after.validator';
import { createBeforeValidator } from '@/modules/dynamic-form/validations/date/before.validator';
import { createListValidatorsMap } from '@/modules/dynamic-form/validations/list/createListValidatorsMap';
import { createNonEmptyValidator } from '@/modules/dynamic-form/validations/list/nonEmpty.validator';
import { createScheduledOrBlanketSubFieldValidatorsMap } from '@/modules/dynamic-form/validations/scheduled-or-blanket/createScheduledOrBlanketSubFieldValidatorsMap';

function addHardcodedValidators({
    schema,
    fieldsMeta,
    getPolicyExpirationDateValue,
    getPolicyEffectiveDateValue,
    customMessages,
}: {
    schema: FieldSchema;
    fieldsMeta: Record<FieldPath, FieldMeta>;
    getPolicyExpirationDateValue: () => any;
    getPolicyEffectiveDateValue: () => any;
    customMessages?: Record<ValidationRules, string>;
}): FieldValidator[] {
    const validators: FieldValidator[] = [];
    if (schema.path === 'policy.effectiveDate') {
        const expirationDateSchema = fieldsMeta[toFieldPath('policy.expirationDate')];
        if (expirationDateSchema) {
            validators.push(createBeforeValidator(
                getPolicyExpirationDateValue,
                { fieldName: schema.label,
                    beforeField: expirationDateSchema.label,
                    customMsg: customMessages?.['date.before'],
                },
            ));
        }
    } else if (schema.path === 'policy.expirationDate') {
        const effectiveDateSchema = fieldsMeta[toFieldPath('policy.effectiveDate')];
        if (effectiveDateSchema) {
            validators.push(createAfterValidator(
                getPolicyEffectiveDateValue,
                {
                    fieldName: schema.label,
                    afterField: effectiveDateSchema.label,
                    customMsg: customMessages?.['date.after'],
                },
            ));
        }
    }
    return validators;
}

export function createValidators({
    schemas,
    fieldsMeta,
    getPolicyExpirationDateValue,
    getPolicyEffectiveDateValue,
    customMessages,
}: {
    schemas: FieldSchema[];
    fieldsMeta: Record<FieldPath, FieldMeta>;
    getPolicyExpirationDateValue: () => any;
    getPolicyEffectiveDateValue: () => any;
    customMessages?: Record<ValidationRules, string>;
}): UnifiedFieldRecord<FieldValidator[]> {
    return schemas.reduce(
        (acc, schema) => {
            // for now only support one level fields up to Object of primative, and list of primative
            // anymore complex will be used special handling until we have better dynamic handling.
            if (schema.type.$type === 'Address') {
                acc[toFieldPath(schema.path)] = createAddressSubFieldValidatorsMap(true, customMessages);
            } else if (schema.type.$type === 'ScheduledOrBlanket') {
                acc[toFieldPath(schema.path)] = createScheduledOrBlanketSubFieldValidatorsMap(true, customMessages);
            } else if (schema.type.$type === 'List' &&
                 (schema.type as TypeDefinitionList).elementType.$type === 'Collateral'
            ) {
                const collateralValidatorsMap = createCollateralSubFieldValidatorsMap(true, customMessages);
                acc[toFieldPath(schema.path)] = {
                    $self: [
                        createRequiredValidator(true, { fieldName: schema.label, customMsg: customMessages?.required }),
                        createNonEmptyValidator({ fieldName: schema.label }),
                    ],
                    ...mapKeys(collateralValidatorsMap, (_, key) => `$item.${key}`),
                };
            } else if (schema.type.$type === 'List') {
                const listType = schema.type as TypeDefinitionList;
                const elementType = listType.elementType.$type as FieldType;
                acc[toFieldPath(schema.path)] = createListValidatorsMap({
                    label: schema.label,
                    nullable: true,
                    required: true,
                    // this maynot set correctly by BE atm, DDR fields are required, set non empty for all list
                    // nonEmpty: listType.nonEmpty || false,
                    nonEmpty: true,
                    itemType: elementType,
                    customMessages,
                }) as Record<UnifiedFieldRecordKey, FieldValidator[]>;
            } else {
                const validators: FieldValidator[] = [];
                validators.push(createRequiredValidator(
                    true, { customMsg: customMessages?.required, fieldName: schema.label },
                ));
                validators.push(...createDefaultValidators(schema, customMessages));
                validators.push(...addHardcodedValidators({
                    schema,
                    fieldsMeta,
                    getPolicyExpirationDateValue,
                    getPolicyEffectiveDateValue,
                    customMessages,
                }));
                acc[toFieldPath(schema.path)] = { $self: validators };
            }
            return acc;
        },
        {} as UnifiedFieldRecord<FieldValidator[]>,
    );
}
